/*

Summary:
The function `calculateDBC` computes 5-year Directional Business Case (DBC) TCO results
for source and target environments by calling the dbcsiDBC API.

Description:
1. Calls calculateSource to get source environment 5-year TCO
2. Calls overrideTarget for RDS-Oracle (always)
3. Calls overrideTarget for Aurora PostgreSQL (if totalAuroracost > 0)
4. Calls overrideTarget for Consolidated RDS-Oracle (if totalRDSConsolidatedcost > 0)
5. Calls overrideTarget for ODB@AWS (if ODB is configured)
6. Returns all results

Parameters:
1. API (object): The aws-amplify API module
2. total (object): The totals object for the scenario (totals[scenarioIndex])
3. adv (object): Advanced settings { total_vcpus, annual_growth_percentage, business_benefit_amount, add_discounts, discount_percentage }
4. exadataParams (object, optional): If provided, triggers Exadata source calculation
   { exadata_version, exadata_racks, exadata_db_servers, exadata_storage_servers, exadata_storage_high_capacity }

Returns an object with:
   - sourceResult, exadataSourceResult, targetResult, auroraTargetResult, consolidatedTargetResult, odbTargetResult
   - error (string or null)

*/

function hasODB(total) {
  if (!total.odb_target_option_tag) return false;
  if (total.odb_target_option_tag.startsWith('odb_adbs')) return total.totalADBSCost > 0;
  return total.totalODBcost > 0;
}

async function calculateDBC(API, total, adv, exadataParams) {
  const results = {
    sourceResult: null,
    exadataSourceResult: null,
    targetResult: null,
    auroraTargetResult: null,
    consolidatedTargetResult: null,
    odbTargetResult: null,
    error: null,
  };

  try {
    // Common target params
    const commonSource = {
      source_location: "on-premises",
      source_engine: "Oracle",
      source_edition: "Enterprise",
      total_vcpus: adv.total_vcpus,
      storage_gb: total.totalSourceUsableDiskSpace,
      annual_growth_percentage: adv.annual_growth_percentage,
    };
    const commonTarget = {
      ...commonSource,
      add_business_benefits: adv.business_benefit_amount > 0,
      business_benefit_amount: adv.business_benefit_amount * 5,
      add_discounts: adv.discount_percentage > 0,
      discount_percentage: adv.discount_percentage,
      labor_vcpu: adv.total_vcpus,
    };

    const post = (formData) => API.post("dbcsiAPI", "/dbcsiDBC", { body: { formData } });

    // Call 1: calculateSource
    const sourceResp = await post({
      action: "calculateSource",
      ...commonSource,
      labor_vcpu: adv.total_vcpus,
    });
    // console.log("DBC source", sourceResp.response);
    results.sourceResult = sourceResp.response;

    // Call 1b: calculateSource for Exadata (if applicable)
    if (exadataParams) {
      const exaResp = await post({
        action: "calculateSource",
        source_location: "on-premises",
        source_engine: "Oracle Exadata",
        source_edition: "Enterprise",
        total_vcpus: adv.total_vcpus,
        storage_gb: total.totalSourceUsableDiskSpace,
        annual_growth_percentage: adv.annual_growth_percentage,
        labor_vcpu: adv.total_vcpus,
        exadata_version: exadataParams.exadata_version,
        exadata_racks: exadataParams.exadata_racks,
        exadata_db_servers: exadataParams.exadata_db_servers,
        exadata_storage_servers: exadataParams.exadata_storage_servers,
        exadata_storage_high_capacity: exadataParams.exadata_storage_high_capacity,
      });
      results.exadataSourceResult = exaResp.response;
    }

    // Call 2: overrideTarget for RDS-Oracle
    const targetResp = await post({
      action: "overrideTarget",
      ...commonTarget,
      target_option_tag: total.rds_target_option_tag,
      target_vcpus: total.totalRDSVCPU,
      target_compute_price: total.totalRDSComputecost * 12,
      target_storage_price: total.totalRDSStoragecost * 12,
    });
    // console.log("DBC target", targetResp.response);
    results.targetResult = targetResp.response;

    // Call 3: Aurora PostgreSQL
    if (total.totalAuroracost > 0) {
      const auroraResp = await post({
        action: "overrideTarget",
        ...commonTarget,
        target_option_tag: "aurora_postgres",
        target_vcpus: total.totalAuroraVCPU,
        target_compute_price: total.totalAuroraComputecost * 12,
        target_storage_price: total.totalAuroraStoragecost * 12,
      });
      // console.log("DBC aurora target", auroraResp.response);
      results.auroraTargetResult = auroraResp.response;
    }

    // Call 4: Consolidated RDS-Oracle
    if (total.totalRDSConsolidatedcost > 0) {
      const consolidatedResp = await post({
        action: "overrideTarget",
        ...commonTarget,
        target_option_tag: total.rds_target_option_tag,
        target_vcpus: total.totalRDSConsolidatedVCPU,
        target_compute_price: total.totalRDSConsolidatedComputecost * 12,
        target_storage_price: total.totalRDSConsolidatedStoragecost * 12,
      });
      // console.log("DBC consolidated target", consolidatedResp.response);
      results.consolidatedTargetResult = consolidatedResp.response;
    }

    // Call 5: ODB@AWS
    if (hasODB(total)) {
      const isAdbs = total.odb_target_option_tag.startsWith('odb_adbs');
      const odbResp = await post({
        action: "overrideTarget",
        ...commonTarget,
        target_option_tag: total.odb_target_option_tag,
        target_vcpus: isAdbs ? Math.round(total.totalADBSEcpus / 2) : Math.round(total.totalODBUsage / 2),
        target_compute_price: isAdbs ? total.totalADBSEcpuCost * 12 : (total.totalODBdbservercost + total.totalODBUsagecost) * 12,
        target_storage_price: isAdbs ? total.totalADBSStorageCost * 12 : total.totalODBstorageservercost * 12,
      });
      // console.log("DBC odb target", odbResp.response);
      results.odbTargetResult = odbResp.response;
    }
  } catch (e) {
    results.error = "Error calculating business case: " + (e.message || e);
    console.log("DBC", results.error);
  }

  return results;
}

export { calculateDBC };
